import type {Evaluator, Version} from "@cm/graph-v3"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {versionChain, graphNode} from "@cm/graph-v3"
import {isInstanceOf} from "@cm/utils"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {TemplateNode} from "#template/template-node/template-node"
import {TemplateNodes} from "#template/nodes/template-nodes"
import {ConfigVariant} from "#template/nodes/owner/config-variant"
import {externalId} from "#template/nodes/validation"
import type {ExternalId} from "#template/nodes/types"
import type {NodeOwnerLikeNode} from "#template/role-markers"
import {nodeOwnerMarker} from "#template/role-markers"
import type {NodeEvaluator} from "#template/node-evaluator"
import {NotReady} from "#template/runtime-graph/slots"
import type {VariantInfo} from "#template/interface-descriptors"
import {ConfigInfo} from "#template/interface-descriptors"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {BuilderOutlet} from "#template/runtime-graph/graph-builder"
import {visitNone, visitAll} from "#template/template-node/visit-helpers"
import {collectAffectedParts} from "#template/fundamental/helpers"

const TD = TypeDescriptors

const affectedObjectPathPartsDesc = TD.Array(TD.ObjectPathPart)
const affectedOutputIdsDesc = TD.Array(TD.String)
const variantInfoDesc = TD.ShallowJSON<VariantInfo>()

export type ConfigGroupParameters = {
    name: string
    id: ExternalId
    nodes: TemplateNodes
    displayWithLabels: boolean
    order?: number
    sectionTitle?: string
}

type V0 = {
    name: string
    id: ExternalId
    nodes: TemplateNodes
}

type V1 = V0 & {displayWithLabels: boolean}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        return {...parameters, displayWithLabels: false}
    },
}

const configGroupDefinition: TemplateNodeDefinition<ConfigGroupParameters> = {
    nodeClass: "ConfigGroup",
    validation: {
        parameters: z.object({
            name: z.string(),
            id: externalId,
            nodes: graphNode(TemplateNodes),
            displayWithLabels: z.boolean(),
            order: z.optional(z.number()),
            sectionTitle: z.optional(z.string()),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0]),
}

@registerTemplateGraphNode
export class ConfigGroup extends TemplateNode<ConfigGroupParameters> implements NodeOwnerLikeNode {
    get [nodeOwnerMarker]() {
        return true as const
    }

    public override get definition() {
        return configGroupDefinition
    }

    public override onFilterActive(context: OnFilterActiveContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ConfigGroupParameters> {
        const {nodes, ...rest} = this.parameters

        const activeConfigVariant = this.getActiveConfigVariant(context.evaluator)

        return {
            nodes: nodes.visit(evaluator, (subNode): boolean => {
                if (subNode instanceof ConfigVariant) {
                    return subNode === activeConfigVariant
                } else return true
            }),
            ...visitNone(rest),
        }
    }

    public override onCompile(context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ConfigGroupParameters> {
        const {id, name, displayWithLabels, order, sectionTitle} = this.parameters
        const {evaluator: nodeEvaluator, currentTemplate, path} = context
        const {activeNodeSet} = nodeEvaluator
        const {descriptorList} = currentTemplate

        const configVariants = this.getConfigVariants()
        const activeConfigVariant = activeNodeSet ? configVariants.find((configVariant) => activeNodeSet.has(configVariant)) : undefined

        const scope = nodeEvaluator.getScope(this)

        const variants = scope.list(
            configVariants.map<BuilderOutlet<VariantInfo>>((variant, index) => {
                const {id, name, iconColor, iconDataObject} = variant.parameters
                const iconDataObjectId = iconDataObject?.parameters.dataObjectId

                // Keyed by index, not the user-editable variant id (ids can collide / change).
                return scope.value({id, name, iconColor, iconDataObjectId}, variantInfoDesc, `variantInfo-${index}`)
            }),
        )

        const activeVariant = scope.pureLambda(
            scope.tuple(variants, activeConfigVariant),
            ([variants, activeConfigVariant]) => variants.find((variant) => variant.id === activeConfigVariant?.parameters.id),
            "activeVariant",
        )

        const descendants = new Set<TemplateNode>()
        const collectDescendants = (node: TemplateNode) => {
            if (descendants.has(node)) return
            descendants.add(node)
            node.children.forEach((child) => {
                if (child instanceof TemplateNode) collectDescendants(child)
            })
        }
        collectDescendants(this)
        const {parts, affectedOutputIds} = collectAffectedParts(descendants, activeNodeSet)
        const affectedObjectPathParts = scope.value(
            parts.map(({templateNode: n, part}) => ({path: [...path, n], part})),
            affectedObjectPathPartsDesc,
            "affectedObjectPathParts",
        )
        const affectedOutputIdsValue = scope.value(affectedOutputIds, affectedOutputIdsDesc, "affectedOutputIds")

        descriptorList.push(
            scope.pureLambda(
                scope.tuple(id, name, variants, activeVariant, displayWithLabels, order, affectedObjectPathParts, affectedOutputIdsValue, this, sectionTitle),
                ([id, name, variants, activeVariant, displayWithLabels, order, affectedObjectPathParts, affectedOutputIds, origin, sectionTitle]) => {
                    return new ConfigInfo({
                        id,
                        name,
                        variants,
                        value: activeVariant ?? null,
                        type: "input",
                        displayWithLabels,
                        origin,
                        order,
                        sectionTitle,
                        affectedObjectPathParts,
                        affectedOutputIds,
                    })
                },
                "configInfo",
            ),
        )

        return visitAll(this.parameters, evaluator)
    }

    getConfigVariants = () => this.parameters.nodes.parameters.list.filter(isInstanceOf(ConfigVariant))

    getActiveConfigVariant = (evaluator: NodeEvaluator) => {
        const selectedId = this.getTemplateInput(evaluator)
        const activeConfigVariant = this.getConfigVariants().find((configVariant) => {
            if (selectedId === undefined) {
                return true
            } else if (configVariant.parameters.id === selectedId) return true
            return false
        })
        return activeConfigVariant ?? null
    }

    getTemplateInput(evaluator: NodeEvaluator) {
        const externalId = this.parameters.id
        const {ambientInputs} = evaluator
        const entry = ambientInputs[externalId]
        if (!entry) return undefined

        if (entry === NotReady) throw Error(`ConfigGroup parameter ${this.parameters.name} has been evaluated before it was ready`)

        if (entry.type === "String") return entry.value as string
        else throw Error(`ConfigGroup parameter ${this.parameters.name} is not a string`)
    }
}
